/**
 * Temple / Pagoda (道观) - Multi-tiered tower with magic flame
 * Features: 5-tier pagoda, blue-gray roofs with decorative eaves, blue magic flame at top
 * Size: 3x3 tiles (visual ~2.6 x 2.6, centered)
 */

import * as THREE from 'three';

export function createTempleMesh(
  group: THREE.Group,
  mainColor: THREE.Color,
  accentColor: THREE.Color
): void {
  const stoneMat = new THREE.MeshStandardMaterial({ color: 0x6a6a6a, roughness: 0.85 });
  const darkStoneMat = new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.9 });
  const wallMat = new THREE.MeshStandardMaterial({ color: 0xd5cec5, roughness: 0.7 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0x37474f, roughness: 0.6 });
  const darkRoofMat = new THREE.MeshStandardMaterial({ color: 0x263238, roughness: 0.5 });
  const doorMat = new THREE.MeshStandardMaterial({ color: 0x8b0000, roughness: 0.7 });
  const windowMat = new THREE.MeshStandardMaterial({ color: 0x1a237e, emissive: 0x0d47a1, emissiveIntensity: 0.15 });
  const flameMat = new THREE.MeshStandardMaterial({
    color: 0x4fc3f7,
    emissive: 0x29b6f6,
    emissiveIntensity: 0.8
  });
  const flameCoreMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    emissive: 0x81d4fa,
    emissiveIntensity: 1.0
  });

  // === Stone Platform Base (2.6 x 2.6, centered) ===
  const base1Geom = new THREE.BoxGeometry(2.6, 0.3, 2.6);
  const base1 = new THREE.Mesh(base1Geom, darkStoneMat);
  base1.position.y = 0.15;
  base1.receiveShadow = true;
  base1.castShadow = true;
  group.add(base1);

  const base2Geom = new THREE.BoxGeometry(2.3, 0.25, 2.3);
  const base2 = new THREE.Mesh(base2Geom, stoneMat);
  base2.position.y = 0.42;
  base2.castShadow = true;
  group.add(base2);

  const base3Geom = new THREE.BoxGeometry(2.0, 0.2, 2.0);
  const base3 = new THREE.Mesh(base3Geom, darkStoneMat);
  base3.position.y = 0.65;
  base3.castShadow = true;
  group.add(base3);

  // Stone steps (front, within platform bounds)
  for (let i = 0; i < 4; i++) {
    const stepWidth = 0.9 - i * 0.06;
    const stepGeom = new THREE.BoxGeometry(stepWidth, 0.1, 0.18);
    const step = new THREE.Mesh(stepGeom, stoneMat);
    step.position.set(0, 0.05 + i * 0.12, 1.05 + i * 0.15);
    step.castShadow = true;
    group.add(step);
  }

  // === Tier 1 (Largest) ===
  const tier1Geom = new THREE.BoxGeometry(1.5, 0.95, 1.5);
  const tier1 = new THREE.Mesh(tier1Geom, wallMat);
  tier1.position.y = 1.22;
  tier1.castShadow = true;
  group.add(tier1);

  // Tier roof helper function
  const createTierRoof = (width: number, height: number, y: number) => {
    const roofConeGeom = new THREE.ConeGeometry(width * 0.72, height, 4);
    const roofCone = new THREE.Mesh(roofConeGeom, roofMat);
    roofCone.position.y = y + height / 2;
    roofCone.rotation.y = Math.PI / 4;
    roofCone.castShadow = true;
    group.add(roofCone);

    // Eave
    const eaveGeom = new THREE.BoxGeometry(width + 0.2, 0.06, width + 0.2);
    const eave = new THREE.Mesh(eaveGeom, darkRoofMat);
    eave.position.y = y;
    group.add(eave);

    // Corner ornaments
    const cornerOrnGeom = new THREE.ConeGeometry(0.05, 0.1, 4);
    const halfW = width / 2 + 0.08;
    const corners = [
      { x: halfW, z: halfW }, { x: -halfW, z: halfW },
      { x: halfW, z: -halfW }, { x: -halfW, z: -halfW }
    ];
    for (const c of corners) {
      const orn = new THREE.Mesh(cornerOrnGeom, darkRoofMat);
      orn.position.set(c.x, y - 0.06, c.z);
      orn.rotation.x = Math.PI;
      group.add(orn);
    }
  };

  createTierRoof(1.7, 0.35, 1.75);

  // Tier 1 windows
  const windowGeom = new THREE.BoxGeometry(0.2, 0.35, 0.05);
  const windowPositions = [
    { x: 0.35, z: 0.77 }, { x: -0.35, z: 0.77 },
    { x: 0.77, z: 0 }, { x: -0.77, z: 0 }
  ];
  for (const pos of windowPositions) {
    const win = new THREE.Mesh(windowGeom, windowMat);
    win.position.set(pos.x, 1.22, pos.z);
    if (Math.abs(pos.x) > 0.7) win.rotation.y = Math.PI / 2;
    group.add(win);
  }

  // Red door
  const doorGeom = new THREE.BoxGeometry(0.35, 0.55, 0.06);
  const door = new THREE.Mesh(doorGeom, doorMat);
  door.position.set(0, 1.02, 0.77);
  group.add(door);

  // === Tier 2 ===
  const tier2Geom = new THREE.BoxGeometry(1.2, 0.75, 1.2);
  const tier2 = new THREE.Mesh(tier2Geom, wallMat);
  tier2.position.y = 2.47;
  tier2.castShadow = true;
  group.add(tier2);

  createTierRoof(1.35, 0.3, 2.9);

  // Tier 2 windows
  for (const pos of [{ x: 0, z: 0.62 }, { x: 0, z: -0.62 }]) {
    const win = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.28, 0.05), windowMat);
    win.position.set(pos.x, 2.47, pos.z);
    group.add(win);
  }

  // === Tier 3 ===
  const tier3Geom = new THREE.BoxGeometry(0.95, 0.65, 0.95);
  const tier3 = new THREE.Mesh(tier3Geom, wallMat);
  tier3.position.y = 3.52;
  tier3.castShadow = true;
  group.add(tier3);

  createTierRoof(1.1, 0.26, 3.9);

  // Tier 3 windows
  for (const pos of [{ x: 0, z: 0.49 }, { x: 0, z: -0.49 }]) {
    const win = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.24, 0.05), windowMat);
    win.position.set(pos.x, 3.52, pos.z);
    group.add(win);
  }

  // === Tier 4 ===
  const tier4Geom = new THREE.BoxGeometry(0.75, 0.55, 0.75);
  const tier4 = new THREE.Mesh(tier4Geom, wallMat);
  tier4.position.y = 4.43;
  tier4.castShadow = true;
  group.add(tier4);

  createTierRoof(0.9, 0.22, 4.75);

  // === Tier 5 (Top) ===
  const tier5Geom = new THREE.BoxGeometry(0.55, 0.45, 0.55);
  const tier5 = new THREE.Mesh(tier5Geom, wallMat);
  tier5.position.y = 5.2;
  tier5.castShadow = true;
  group.add(tier5);

  createTierRoof(0.7, 0.2, 5.47);

  // === Spire ===
  const spireGeom = new THREE.ConeGeometry(0.1, 0.35, 8);
  const spire = new THREE.Mesh(spireGeom, darkRoofMat);
  spire.position.y = 5.85;
  group.add(spire);

  // Spire ornament
  const spireOrnGeom = new THREE.SphereGeometry(0.06, 8, 6);
  const spireOrn = new THREE.Mesh(spireOrnGeom, new THREE.MeshStandardMaterial({
    color: 0xd4af37,
    metalness: 0.7,
    roughness: 0.3
  }));
  spireOrn.position.y = 6.05;
  group.add(spireOrn);

  // === Blue Magic Flame ===
  // Outer flame
  const flameOuterGeom = new THREE.ConeGeometry(0.16, 0.5, 8);
  const flameOuter = new THREE.Mesh(flameOuterGeom, flameMat);
  flameOuter.position.y = 6.38;
  group.add(flameOuter);

  // Inner flame (brighter)
  const flameInnerGeom = new THREE.ConeGeometry(0.1, 0.38, 8);
  const flameInner = new THREE.Mesh(flameInnerGeom, flameCoreMat);
  flameInner.position.y = 6.35;
  group.add(flameInner);

  // Flame glow sphere
  const glowGeom = new THREE.SphereGeometry(0.12, 8, 6);
  const glow = new THREE.Mesh(glowGeom, flameMat);
  glow.position.y = 6.15;
  group.add(glow);

  // Floating particles around flame
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2;
    const radius = 0.28 + Math.sin(i * 2) * 0.08;
    const particleGeom = new THREE.SphereGeometry(0.03, 6, 4);
    const particle = new THREE.Mesh(particleGeom, flameMat);
    particle.position.set(
      Math.cos(angle) * radius,
      6.2 + Math.sin(i * 1.7) * 0.15,
      Math.sin(angle) * radius
    );
    group.add(particle);
  }

  // === Decorative Details ===
  // Platform edge stones (within bounds)
  const edgeStoneGeom = new THREE.BoxGeometry(0.15, 0.15, 0.15);
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const radius = 1.15;
    const edgeStone = new THREE.Mesh(edgeStoneGeom, stoneMat);
    edgeStone.position.set(
      Math.cos(angle) * radius,
      0.62,
      Math.sin(angle) * radius
    );
    edgeStone.rotation.y = angle;
    group.add(edgeStone);
  }

  // Lanterns on first tier (inside bounds)
  const lanternMat = new THREE.MeshStandardMaterial({
    color: 0xc62828,
    emissive: 0xff3d00,
    emissiveIntensity: 0.3
  });
  const lanternPositions = [
    { x: 0.55, z: 0.8 }, { x: -0.55, z: 0.8 }
  ];
  for (const pos of lanternPositions) {
    const lanternGeom = new THREE.CylinderGeometry(0.05, 0.06, 0.12, 6);
    const lantern = new THREE.Mesh(lanternGeom, lanternMat);
    lantern.position.set(pos.x, 1.65, pos.z);
    group.add(lantern);

    // Lantern top
    const topGeom = new THREE.CylinderGeometry(0.03, 0.04, 0.03, 6);
    const top = new THREE.Mesh(topGeom, new THREE.MeshStandardMaterial({ color: 0x5d4037 }));
    top.position.set(pos.x, 1.73, pos.z);
    group.add(top);
  }

  // Incense burner at front (inside platform)
  const burnerGeom = new THREE.CylinderGeometry(0.12, 0.15, 0.2, 8);
  const burnerMat = new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.7 });
  const burner = new THREE.Mesh(burnerGeom, burnerMat);
  burner.position.set(0, 0.85, 1.0);
  group.add(burner);

  // Smoke from incense
  const smokeMat = new THREE.MeshStandardMaterial({
    color: 0xaaaaaa,
    transparent: true,
    opacity: 0.4
  });
  for (let i = 0; i < 3; i++) {
    const smokeGeom = new THREE.SphereGeometry(0.03 - i * 0.006, 5, 4);
    const smoke = new THREE.Mesh(smokeGeom, smokeMat);
    smoke.position.set(0, 1.0 + i * 0.1, 1.0);
    group.add(smoke);
  }
}
